import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Icon, Popover, TextField, ColorPicker } from "@shopify/polaris";
import { PlusIcon } from "@shopify/polaris-icons";
import { hslaToHex, hexToHsla, lightenHexColor, darkenHexColor } from "../../../../utilities/parsers";

interface BrandColorPickerProps {
    disabled?: boolean;
}

const BrandColorPicker = ({ disabled = false }: BrandColorPickerProps) => {
    const { setValue, getValues } = useFormContext();

    const [popoverActive, setPopoverActive] = useState(false);
    const togglePopoverActive = useCallback(() => setPopoverActive((active) => !active), []);
    const [color, setColor] = useState({ hue: 120, brightness: 1, saturation: 1 });
    const [hexColor, setHexColor] = useState("");

    const handleColorChange = (newColor: any) => {
        setColor(newColor);
        const { hue, brightness, saturation } = newColor;
        const hslaColor = `hsla(${hue}, ${saturation * 100}%, ${brightness * 100}%, 1)`;
        const hex = hslaToHex(hslaColor);
        setHexColor(hex);

        const newTemp = {
            add_to_cart_bg_color: hex,
            add_to_cart_border_color: darkenHexColor(hex, 20),
            add_to_cart_hover_bg_color: darkenHexColor(hex, 20),
            bundle_title_color: hex,
            border_color: "#050505",
            product_background_color: lightenHexColor(hex, 70),
            product_border_color: hex,
            special_text_background_color: hex,
            special_text_color: newColor.brightness >= 0.5 ? "#050505" : "#ffffff",
            discount_label_background_color: hex,
            discount_label_color: newColor.brightness >= 0.5 ? "#050505" : "#ffffff",
            free_gift_header_bg_color: hex,
            free_gift_header_color: brightness >= 0.5 ? "#050505" : "#ffffff",
            free_gift_bg_color: lightenHexColor(hex, 70),
            free_gift_text_color: "#303030",
            free_gift_border_color: "#050505",
            free_gift_compare_price_color: "#050505",
            upsell_bg_color: lightenHexColor(hex, 70),
            upsell_text_color: "#303030",
            upsell_price_color: hex,
            upsell_compare_price_color: "#050505",
            quantity_selector_border_color: hex,
            quantity_selector_text_color: hex,
            swatches_select_color: hex,
            variant_selector_text_color: hex,
            show_line_around_title_color: hex,
            timer_background_color: lightenHexColor(hex, 70),
            hover_color: hex,
            discount_label_primary_color: hex,
            discount_label_secondary_color: darkenHexColor(hex, 20),
            savings_summary_text_color: hex,
            savings_summary_background_color: lightenHexColor(hex, 70),
        };

        const currentBundlesDesign = getValues("bundles_design") || {};
        setValue("bundles_design", { ...currentBundlesDesign, ...newTemp }, { shouldDirty: true });
    };

    return (
        <Popover
            active={popoverActive && !disabled}
            preferredAlignment="right"
            activator={
                <Button size="micro" disabled={disabled} onClick={togglePopoverActive}>
                    {/* @ts-expect-error no react elements allowed */}
                    <div
                        style={{
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontWeight: "500",
                            position: "relative",
                        }}
                    >
                        <span
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "20px",
                                height: "20px",
                                border: "2px dotted #d2d5db",
                                borderRadius: "50%",
                            }}
                        >
                            <Icon source={PlusIcon} />
                        </span>
                        <span>Brand Color</span>
                    </div>
                </Button>
            }
            onClose={togglePopoverActive}
        >
            <div
                style={{
                    backgroundColor: "#ffffff",
                    padding: "12px",
                    borderRadius: "8px",
                    zIndex: 10,
                    border: "1px solid lightgray",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                <ColorPicker onChange={handleColorChange} color={color} />
                <TextField
                    label=""
                    autoComplete="off"
                    placeholder="#ffffff"
                    maxLength={7}
                    value={hexColor}
                    onChange={(val) => {
                        setHexColor(val);
                        if (val.length == 7 && val.startsWith("#")) {
                            handleColorChange(hexToHsla(val));
                        }
                    }}
                />
            </div>
        </Popover>
    );
};

export default BrandColorPicker;
